import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  Button,
  Checkbox,
  Icon,
  SearchBar,
  Toast,
  type Column,
  type ToastType,
} from "@legal-500/legal500-design-system";
import { docParsing, type UnparsedListFile } from "@/api/docParsing.service";
import type { LegacyPublication } from "@/api/legacy-publications.service";
import { submissionsService, type Submission, type TaxonomyMode } from "@/api/submissions.service";
import {
  BulkParseProgressModal,
  type BulkParsePhase,
} from "@/components/Modal/BulkParseProgressModal";
import { DataFilters } from "@/components/DataFilters/DataFilters";
import { DataTable } from "@/components/DataTable/DataTable";
import EmptyState from "@/components/EmptyState";
import { ExportProgress } from "@/components/ExportProgress/ExportProgress";
import LoadingState from "@/components/LoadingState";
import { DeleteSubmissionModal } from "@/components/DeleteSubmissionModal";
import { PageHeader } from "@/components/PageHeader";
import { PaginationControls } from "@/components/PaginationControls";
import PermissionGate from "@/components/Permissions/PermissionGate";
import { SubmissionFileLink } from "@/components/SubmissionFileLink";
import { TaxonomyToggle } from "@/components/TaxonomyToggle";
import { UnauthorizedView } from "@/components/UnauthorizedView";
import { useExport } from "@/hooks/useExport";
import { useFeatureFlags } from "@/hooks/useFeatureFlags";
import { useFilters } from "@/hooks/useFilters";
import { usePermissions } from "@/hooks/usePermissions";
import { usePublications } from "@/hooks/usePublications";
import { useSubmissionUniques } from "@/hooks/useSubmissionUniques";
import { useSubmissions } from "@/hooks/useSubmissions";
import { TAXONOMY_URL_PARAM, useTaxonomyMode } from "@/hooks/useTaxonomyMode";
import { useTranslations, type SubmissionWithTranslations } from "@/hooks/useTranslations";
import { useUrlParams } from "@/hooks/useUrlParams";
import { batchTranslateSubmissions } from "@/utils/batchTranslations";
import {
  getSubmissionPracticeFullName,
  getSubmissionPracticeFullNameTranslated,
} from "@/utils/practiceName";
import { formatDateShort } from "@/utils/dateFormatter";
import { withQueryParam } from "@/utils/returnTo";
import {
  publicationOptionLabel,
  taxonomyFilterParams,
  taxonomyOptionFitsMode,
  taxonomyOptionValue,
  taxonomyValueFitsMode,
} from "@/utils/taxonomyFilter";
import styles from "../CompanyUsers/CompanyUsers.module.css";
import submissionStyles from "./SubmissionsList.module.css";
import { generateSubmissionsCsv } from "./SubmissionsList/csvGenerators";
import {
  CONTACT_MATCH_FEATURE_FLAG,
  CONTACT_MATCH_FILTER_OPTIONS,
  CREATED_CONTACT_ID_PARAM,
  ADDRESS_BOOK_READ_PERMISSION,
  LINK_CONTACT_ENTRY_PARAM,
  SUBMISSION_WRITE_PERMISSION,
  contactMatchStatusesFor,
  isLawyerSubmission,
} from "./SubmissionsList/contactMatch";
import { LawyerCell } from "./SubmissionsList/LawyerCell";
import { LinkSubmissionContactModal } from "./SubmissionsList/LinkSubmissionContactModal";
import {
  readSubmissionsListStorage,
  writeSubmissionsListStorage,
} from "./SubmissionsList/submissionsListStorage";
import { useSubmissionDelete } from "./SubmissionsList/useSubmissionDelete";

const CONTACT_MATCH_LABEL_OVERRIDES = { contactMatch: "Contact match" };

function SubmissionsList() {
  const { searchParams, updateUrl } = useUrlParams();

  // All contact-link UI hangs off this flag. hasFlag is false while flags are still
  // loading, so the UI stays hidden until the flag is known to be on.
  const { hasFlag, isLoading: flagsLoading } = useFeatureFlags();
  const contactLinkEnabled = hasFlag(CONTACT_MATCH_FEATURE_FLAG);

  const currentYear = new Date().getFullYear();

  // Read URL first; fall back to sessionStorage; finally fall back to defaults.
  // URL params take priority so that shareable links and browser back/forward
  // continue to work as before. SessionStorage is the safety net for in-app
  // navigation that clears the URL.
  const initialStateRef = useRef<{
    fromStorage: boolean;
    filters: {
      publication: string;
      region: string;
      edition: string;
      practiceArea: string;
      contactMatch: string;
    };
    taxonomy: string | undefined;
    searchTerm: string;
    currentPage: number;
    sortField: string | null;
    sortDirection: "asc" | "desc";
    hideIncomplete: boolean;
  } | null>(null);

  if (initialStateRef.current === null) {
    const stored = readSubmissionsListStorage();
    const pick = (urlKey: string, storageValue: string | undefined, fallback: string): string => {
      const fromUrl = searchParams.get(urlKey);
      if (fromUrl !== null) return fromUrl;
      if (storageValue !== undefined) return storageValue;
      return fallback;
    };
    const urlHadAnyParam = [
      "publication",
      "region",
      "edition",
      "practiceArea",
      "contactMatch",
      "search",
      "page",
      "sortField",
      "sortDirection",
      "hideIncomplete",
      TAXONOMY_URL_PARAM,
    ].some(key => searchParams.get(key) !== null);

    initialStateRef.current = {
      fromStorage: !urlHadAnyParam && Object.keys(stored).length > 0,
      filters: {
        publication: pick("publication", stored.publication, "all"),
        region: pick("region", stored.region, "all"),
        edition: pick("edition", stored.edition, String(currentYear)),
        practiceArea: pick("practiceArea", stored.practiceArea, "all"),
        contactMatch: pick("contactMatch", stored.contactMatch, "all"),
      },
      taxonomy: stored.taxonomy || undefined,
      searchTerm: pick("search", stored.search, ""),
      currentPage: Number(pick("page", stored.page, "1")) || 1,
      sortField: (() => {
        const value = pick("sortField", stored.sortField, "");
        return value === "" ? null : value;
      })(),
      sortDirection: ((): "asc" | "desc" => {
        const value = pick("sortDirection", stored.sortDirection, "asc");
        return value === "desc" ? "desc" : "asc";
      })(),
      hideIncomplete: pick("hideIncomplete", stored.hideIncomplete, "false") === "true",
    };
  }

  const { toggleVisible, mode, taxonomy, ready } = useTaxonomyMode(
    initialStateRef.current.fromStorage ? initialStateRef.current.taxonomy : undefined,
  );
  const [filters, setFilters] = useState(initialStateRef.current.filters);
  const [searchTerm, setSearchTerm] = useState(initialStateRef.current.searchTerm);
  const [currentPage, setCurrentPage] = useState(initialStateRef.current.currentPage);
  const [sortField, setSortField] = useState<string | null>(initialStateRef.current.sortField);
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">(
    initialStateRef.current.sortDirection,
  );
  const ITEMS_PER_PAGE = 50;
  const [hideIncomplete, setHideIncomplete] = useState(initialStateRef.current.hideIncomplete);

  useEffect(() => {
    if (!ready) return;
    const stale = (["publication", "region", "practiceArea"] as const).filter(
      key =>
        !taxonomyValueFitsMode(filters[key], mode) ||
        !taxonomyValueFitsMode(searchParams.get(key) ?? "", mode),
    );
    if (stale.length === 0) return;
    const cleared = Object.fromEntries(stale.map(key => [key, "all"]));
    setFilters(prev => ({ ...prev, ...cleared }));
    updateUrl(cleared);
  }, [ready, mode, filters.publication, filters.region, filters.practiceArea]);

  // If state was restored from sessionStorage (URL was empty), push the
  // restored values back into the URL so it stays consistent with the UI.
  // updateUrl strips "all", "false", and empty strings automatically.
  useEffect(() => {
    if (!ready || !initialStateRef.current?.fromStorage) return;
    const restored = initialStateRef.current;
    const fitting = (value: string) => (taxonomyValueFitsMode(value, mode) ? value : "all");
    updateUrl({
      publication: fitting(restored.filters.publication),
      region: fitting(restored.filters.region),
      edition: restored.filters.edition,
      practiceArea: fitting(restored.filters.practiceArea),
      contactMatch: restored.filters.contactMatch,
      search: restored.searchTerm,
      page: String(restored.currentPage),
      sortField: restored.sortField ?? "",
      sortDirection: restored.sortField ? restored.sortDirection : "",
      hideIncomplete: restored.hideIncomplete ? "true" : "false",
      [TAXONOMY_URL_PARAM]: toggleVisible && mode === "new" ? mode : "",
    });
  }, [ready]);

  // Persist current state to sessionStorage on every relevant change so the
  // next visit to this page can rehydrate even if the URL has been cleared.
  useEffect(() => {
    if (!ready) return;
    writeSubmissionsListStorage({
      publication: filters.publication,
      region: filters.region,
      edition: filters.edition,
      practiceArea: filters.practiceArea,
      contactMatch: filters.contactMatch,
      search: searchTerm,
      page: String(currentPage),
      sortField: sortField ?? "",
      sortDirection: sortField ? sortDirection : "",
      hideIncomplete: hideIncomplete ? "true" : "false",
      taxonomy: taxonomy ?? "",
    });
  }, [ready, filters, searchTerm, currentPage, sortField, sortDirection, hideIncomplete, taxonomy]);

  const { activeFilters, handleFilterChange, handleClearFilter } = useFilters({
    filters,
    setFilters,
    updateUrl,
    labelOverrides: CONTACT_MATCH_LABEL_OVERRIDES,
  });

  const { publicationId, editionId } = taxonomyFilterParams(
    "publication",
    filters.publication,
    mode,
  );
  const { regionId, rankingEntityId } = taxonomyFilterParams("region", filters.region, mode);
  const { practiceAreaId, bpaId } = taxonomyFilterParams(
    "practiceArea",
    filters.practiceArea,
    mode,
  );

  // The review queue (any Contact match filter) only covers approved accounts (DEV-2662).
  // With the flag off a stored filter value is ignored here and cleared below.
  const contactMatchStatus = contactLinkEnabled
    ? contactMatchStatusesFor(filters.contactMatch)
    : undefined;
  const verifiedFirmOnly = contactMatchStatus ? true : undefined;

  // Flag known to be off: drop a Contact match value restored from the URL or
  // sessionStorage, so it can't come back into effect or linger in the URL.
  useEffect(() => {
    if (flagsLoading || contactLinkEnabled || filters.contactMatch === "all") return;
    setFilters(prev => ({ ...prev, contactMatch: "all" }));
    updateUrl({ contactMatch: "" });
  }, [flagsLoading, contactLinkEnabled, filters.contactMatch]);

  const { data: publicationsData } = usePublications(
    { edition: filters.edition },
    { enabled: mode === "legacy" },
  );

  const { data: publicationUniques } = useSubmissionUniques(
    "publication",
    { edition: filters.edition, taxonomy },
    { enabled: mode === "new" },
  );

  const { data: regionUniques } = useSubmissionUniques("region", {
    edition: filters.edition,
    publicationId,
    editionId,
    taxonomy,
  });

  const { data: practiceAreaUniques } = useSubmissionUniques("practiceArea", {
    edition: filters.edition,
    publicationId,
    editionId,
    regionId,
    rankingEntityId,
    taxonomy,
  });

  const { data, loading, error, refetch } = useSubmissions({
    limit: ITEMS_PER_PAGE,
    offset: (currentPage - 1) * ITEMS_PER_PAGE,
    publicationId,
    edition: filters.edition,
    regionId,
    practiceAreaId,
    editionId,
    rankingEntityId,
    bpaId,
    taxonomy,
    search: searchTerm || undefined,
    sortField: sortField ?? undefined,
    sortDirection,
    contactMatchStatus,
    verifiedFirmOnly,
  });

  const submissions = useMemo(() => data?.submissions || [], [data?.submissions]);
  const pagination = data?.pagination || {
    total: 0,
    limit: ITEMS_PER_PAGE,
    offset: 0,
  };

  const isAuthError =
    error &&
    ((error as Error & { status?: number }).status === 401 ||
      (error as Error & { status?: number }).status === 403);

  // Fetch translations for submissions
  const {
    data: submissionsWithTranslations,
    loading: translationsLoading,
    error: translationsError,
  } = useTranslations({
    entities: submissions,
    enabled: !loading && !isAuthError && submissions.length > 0,
    // Per entry, not per submission: rows are one per lawyer entry, and keying by
    // submissionId collapsed a submission's entries into copies of its last one.
    getEntityId: submission => submission.submissionEntryId ?? submission.submissionId,
  });

  const sortableColumnKeys = [
    "firmName",
    "publicationName",
    "edition",
    "regionName",
    "createdAt",
    "submissionType",
    "refereeType",
  ];

  const handleSort = (field: string) => {
    if (field !== sortField) {
      setSortField(field);
      setSortDirection("asc");
      setCurrentPage(1);
      updateUrl({ sortField: field, sortDirection: "asc", page: "1" });
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
      setCurrentPage(1);
      updateUrl({ sortField: field, sortDirection: "desc", page: "1" });
    } else {
      setSortField(null);
      setSortDirection("asc");
      setCurrentPage(1);
      updateUrl({ sortField: "", sortDirection: "", page: "1" });
    }
  };

  // Combined loading state
  const isLoading = loading || translationsLoading;
  const displaySubmissions =
    submissionsWithTranslations.length > 0 ? submissionsWithTranslations : submissions;

  const filteredSubmissions = useMemo(() => {
    if (!hideIncomplete) return displaySubmissions;

    return displaySubmissions.filter(submission => {
      const hasFile = !!submission.fileUri;
      const totalReferees =
        (submission.totalReferees ??
          // fallback for older shapes
          (submission as SubmissionWithTranslations).referee ??
          0) ||
        0;

      const hasReferees = totalReferees > 0;
      return hasFile && hasReferees;
    });
  }, [displaySubmissions, hideIncomplete]);

  const submissionsExport = useExport<Submission, SubmissionWithTranslations>({
    fetchData: async ({ limit, offset }) => {
      const resp = await submissionsService.get({
        limit,
        offset,
        publicationId,
        edition: filters.edition,
        regionId,
        editionId,
        rankingEntityId,
        taxonomy,
        contactMatchStatus,
        verifiedFirmOnly,
      });
      return {
        data: resp.submissions ?? [],
        pagination: resp.pagination,
      };
    },
    processData: batchTranslateSubmissions,
    generateCsv: rows =>
      generateSubmissionsCsv(rows, { includeContactColumns: contactLinkEnabled }),
    getFilename: () => `submissions-export-${new Date().toISOString().split("T")[0]}.csv`,
    emptyMessage: "No submissions found to export",
    errorMessage: "Failed to export submissions",
  });

  // --- Re-parse controls (per-row + bulk) ---
  const BATCH_SIZE = 5;
  const [toast, setToast] = useState<{ message: string; type: ToastType } | null>(null);
  const [parsingEntryFileId, setParsingEntryFileId] = useState<string | null>(null);

  const notify = (message: string, type: ToastType) => setToast({ message, type });

  // CS-only force-delete flow (SOFTSUPP-312 Case 2), gated by role in the column below.
  const deleteFlow = useSubmissionDelete({ onDeleted: refetch, notify });

  // --- Link contact (DEV-2662) ---
  const { hasPermission } = usePermissions();
  // Both: the PATCH needs staff.submission.write, and the modal's firm/contact lookups go
  // to the Address Book. Today both are granted together (staff.submission.write is on
  // staff.submissions_reviewer only, which also has addressbook.read), but each call needs its own.
  const canLinkContacts =
    contactLinkEnabled &&
    hasPermission(SUBMISSION_WRITE_PERMISSION) &&
    hasPermission(ADDRESS_BOOK_READ_PERMISSION);
  const location = useLocation();
  const [linkContactTarget, setLinkContactTarget] = useState<{
    row: SubmissionWithTranslations;
    preselectedContactId: string | null;
  } | null>(null);

  // "Create new contact" round trip: ContactDetail navigates back here with the entry id
  // we handed it and the new contact's id. Reopen the modal on that entry with the
  // contact preselected, then drop both params so a reload doesn't reopen it.
  const returningEntryId = searchParams.get(LINK_CONTACT_ENTRY_PARAM);
  const createdContactId = searchParams.get(CREATED_CONTACT_ID_PARAM);
  useEffect(() => {
    // Wait for the flag too: with it off the params are dropped without reopening.
    if (!returningEntryId || loading || flagsLoading) return;
    const row = submissions.find(candidate => candidate.submissionEntryId === returningEntryId);
    updateUrl({ [LINK_CONTACT_ENTRY_PARAM]: "", [CREATED_CONTACT_ID_PARAM]: "" });
    if (row && canLinkContacts && isLawyerSubmission(row)) {
      setLinkContactTarget({ row, preselectedContactId: createdContactId });
    } else if (createdContactId && contactLinkEnabled) {
      setToast({
        message: "Contact created. Find the submission and use Link contact to link it.",
        type: "info",
      });
    }
  }, [returningEntryId, createdContactId, loading, flagsLoading, submissions, canLinkContacts]);

  const linkContactReturnTo = linkContactTarget?.row.submissionEntryId
    ? withQueryParam(
        `${location.pathname}${location.search}`,
        LINK_CONTACT_ENTRY_PARAM,
        linkContactTarget.row.submissionEntryId,
      )
    : null;

  // Bulk parse runs through a progress modal with confirm → progress → terminal
  // (done / cancelled / error) phases. The modal is purely presentational; this
  // component owns the batch loop and all the counters it renders.
  const [bulkParse, setBulkParse] = useState<{
    phase: BulkParsePhase;
    files: UnparsedListFile[];
    total: number;
    processed: number;
    enqueued: number;
    skipped: number;
    errorMessage: string | null;
    cancelRequested: boolean;
  } | null>(null);
  // Checked before each next chunk so a cancel stops the loop cleanly after the
  // in-flight chunk; a ref avoids the stale-closure problem inside the loop.
  const bulkParseCancelRef = useRef(false);

  const handleParseEntryFile = async (
    event: React.MouseEvent,
    submissionEntryFileId: string,
  ): Promise<void> => {
    event.stopPropagation();
    setParsingEntryFileId(submissionEntryFileId);
    try {
      await docParsing.parseEntryFile(submissionEntryFileId);
      setToast({ message: "Queued for parsing", type: "success" });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to queue file for parsing";
      setToast({ message, type: "error" });
    } finally {
      setParsingEntryFileId(null);
    }
  };

  // Step 1: fetch the (cheap) list/count of unparsed files matching the current
  // filters, then open the progress modal in its confirm phase. Backend may
  // return 400 ("At least one filter is required" / "Too many files matched…")
  // which we surface in a toast.
  const handleStartBulkParse = async (): Promise<void> => {
    try {
      const response = await docParsing.listUnparsed({
        edition: filters.edition,
        publicationId,
        regionId,
        practiceAreaId,
        editionId,
        rankingEntityId,
        bpaId,
        taxonomy,
        search: searchTerm.trim() || undefined,
      });
      if (response.total === 0) {
        setToast({
          message: "No unparsed files match the current filters.",
          type: "info",
        });
        return;
      }
      setBulkParse({
        phase: "confirm",
        files: response.files,
        total: response.total,
        processed: 0,
        enqueued: 0,
        skipped: 0,
        errorMessage: null,
        cancelRequested: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load unparsed files";
      setToast({ message, type: "error" });
    }
  };

  // Step 2: run the parse in sequential chunks of BATCH_SIZE, updating the
  // progress bar after each chunk. Stops cleanly on cancel and on the first
  // chunk error, keeping the partial counts.
  const handleConfirmBulkParse = async (): Promise<void> => {
    const current = bulkParse;
    if (!current) return;

    bulkParseCancelRef.current = false;
    setBulkParse(prev => (prev ? { ...prev, phase: "progress", cancelRequested: false } : prev));

    const ids = current.files.map(file => file.submissionEntryFileId);
    const chunks: string[][] = [];
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      chunks.push(ids.slice(i, i + BATCH_SIZE));
    }

    let processed = 0;
    let enqueued = 0;
    let skipped = 0;

    for (const chunkIds of chunks) {
      if (bulkParseCancelRef.current) {
        setBulkParse(prev => (prev ? { ...prev, phase: "cancelled" } : prev));
        setToast({ message: `Cancelled — queued ${enqueued} so far`, type: "info" });
        return;
      }

      try {
        const response = await docParsing.parseEntryFiles(chunkIds);
        enqueued += response.enqueuedCount;
        skipped += response.skippedCount;
        processed += chunkIds.length;
        setBulkParse(prev => (prev ? { ...prev, processed, enqueued, skipped } : prev));
      } catch (err) {
        const message = err instanceof Error ? err.message : "Failed to queue files for parsing";
        setBulkParse(prev =>
          prev
            ? { ...prev, phase: "error", processed, enqueued, skipped, errorMessage: message }
            : prev,
        );
        setToast({ message, type: "error" });
        return;
      }
    }

    const skippedSuffix = skipped > 0 ? `, skipped ${skipped}` : "";
    setBulkParse(prev => (prev ? { ...prev, phase: "done", processed, enqueued, skipped } : prev));
    setToast({
      message: `Queued ${enqueued} file(s)${skippedSuffix}`,
      type: "success",
    });
  };

  const handleCancelBulkParseRun = (): void => {
    bulkParseCancelRef.current = true;
    setBulkParse(prev => (prev ? { ...prev, cancelRequested: true } : prev));
  };

  const handleCloseBulkParse = (): void => {
    setBulkParse(null);
  };

  const columns: Column<SubmissionWithTranslations>[] = [
    {
      header: "Firm",
      accessor: "firmName",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Publication",
      accessor: "publicationName",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Edition",
      accessor: "edition",
      render: (value: unknown) => (value === null || value === undefined ? "N/A" : String(value)),
    },
    {
      header: "Region",
      accessor: "regionName",
      render: (_value: unknown, row: SubmissionWithTranslations) => {
        const original = row.regionName || "N/A";
        const translated = row.regionTranslated;

        if (!translated || translated === original) {
          return original;
        }

        return (
          <div className={submissionStyles.translationContainer}>
            <div>{original}</div>
            <div className={submissionStyles.translatedText}>{translated}</div>
          </div>
        );
      },
    },
    {
      header: "Practice",
      accessor: "practiceGroupName",
      render: (_value: unknown, row: SubmissionWithTranslations) => {
        const original = getSubmissionPracticeFullName(row);
        const translated = getSubmissionPracticeFullNameTranslated(row);

        if (!translated || translated === original || translated === "-") {
          return original;
        }

        return (
          <div className={submissionStyles.translationContainer}>
            <div>{original}</div>
            <div className={submissionStyles.translatedText}>{translated}</div>
          </div>
        );
      },
    },
    ...(contactLinkEnabled
      ? [
          {
            header: "Lawyer",
            accessor: "personName" as const,
            render: (_value: unknown, row: SubmissionWithTranslations) => <LawyerCell row={row} />,
          },
        ]
      : []),
    {
      header: "Created At",
      accessor: "createdAt",
      render: (value: unknown) => {
        if (!value) return "-";
        try {
          return formatDateShort(String(value));
        } catch {
          return "-";
        }
      },
    },
    {
      header: "Language Code",
      accessor: "languageCode",
      render: (value: unknown) => (value === null || value === undefined ? "-" : String(value)),
    },
    {
      header: "Submission Type",
      accessor: "submissionType",
      render: (value: unknown) => (value === null || value === undefined ? "-" : String(value)),
    },
    {
      header: "Referee Type",
      accessor: "refereeType",
      render: (value: unknown) => {
        if (!value) return "-";
        const types = {
          firm: "Firm",
          bar: "Bar",
          lawyer: "Lawyer",
          mediator: "Mediator",
        };
        return types[value as keyof typeof types] || String(value);
      },
    },
    {
      header: "File",
      accessor: "fileUri",
      render: (_value: unknown, row: SubmissionWithTranslations) => (
        <SubmissionFileLink fileUri={row.fileUri} className={submissionStyles.fileLink} />
      ),
    },
    {
      header: "Parse",
      accessor: "submissionEntryFileId",
      render: (_value: unknown, row: SubmissionWithTranslations) => {
        const submissionEntryFileId = row.submissionEntryFileId;
        // Only show the action when the row has both a file URI and an entry-file
        // id — without the latter there is nothing to re-parse.
        if (!row.fileUri || !submissionEntryFileId) {
          return "—";
        }

        const isParsing = parsingEntryFileId === submissionEntryFileId;

        return (
          <Button
            size="small"
            variant="outlined"
            disabled={isParsing}
            onClick={(e: React.MouseEvent) => {
              void handleParseEntryFile(e, submissionEntryFileId);
            }}
          >
            <span className={submissionStyles.parseBtnContent}>
              {"↻"} {isParsing ? "Parsing..." : "Parse"}
            </span>
          </Button>
        );
      },
    },
    {
      header: "Total Referees",
      accessor: "totalReferees",
      render: (value: unknown) => (value === null || value === undefined ? "0" : String(value)),
    },
    {
      header: "Total People",
      accessor: "totalPeople",
      render: (value: unknown) => (value === null || value === undefined ? "0" : String(value)),
    },
    {
      header: "Responses",
      accessor: "responseCount",
      render: (value: unknown) => (value === null || value === undefined ? "0" : String(value)),
    },
    {
      header: "Referees",
      accessor: "referee",
      render: (value: unknown) => (value === null || value === undefined ? "0" : String(value)),
    },
    {
      // Link contact: lawyer rows only, any match status, staff.submission.write.
      // Delete: CS-only force-delete action. PermissionGate renders nothing for users
      // without the role (SOFTSUPP-312 Case 2).
      header: "Actions",
      accessor: "submissionId",
      render: (_value: unknown, row: SubmissionWithTranslations) => (
        <div className={submissionStyles.rowActions}>
          {canLinkContacts && isLawyerSubmission(row) && row.submissionEntryId && (
            <Button
              size="small"
              variant="outlined"
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                setLinkContactTarget({ row, preselectedContactId: null });
              }}
              aria-label={`${row.contactId ? "Change contact" : "Link contact"} for ${
                row.personName || "this lawyer"
              }`}
            >
              {row.contactId ? "Change contact" : "Link contact"}
            </Button>
          )}
          <PermissionGate permission="admin.submission.delete">
            <Button
              size="small"
              variant="outlined"
              color="error"
              startIcon={<Icon icon="delete" size="small" />}
              onClick={(e: React.MouseEvent) => {
                e.stopPropagation();
                deleteFlow.openDelete(row);
              }}
            >
              Delete
            </Button>
          </PermissionGate>
        </div>
      ),
    },
  ];

  const editionOptions = useMemo(() => {
    const years: { value: string; label: string }[] = [];
    for (let year = currentYear + 1; year >= 2017; year--) {
      years.push({ value: String(year), label: String(year) });
    }
    return years;
  }, [currentYear]);

  const regionOptions = useMemo(() => {
    const values = regionUniques?.values ?? [];
    return [
      { value: "all", label: "All regions" },
      ...values
        .filter(v => taxonomyOptionFitsMode("region", v, mode))
        .map(v => ({ value: taxonomyOptionValue("region", v, mode), label: v.name })),
    ];
  }, [regionUniques?.values, mode]);

  const practiceAreaOptions = useMemo(() => {
    const values = practiceAreaUniques?.values ?? [];
    return [
      { value: "all", label: "All practice areas" },
      ...values
        .filter(v => taxonomyOptionFitsMode("practiceArea", v, mode))
        .map(v => ({
          value: taxonomyOptionValue("practiceArea", v, mode),
          label: v.practiceGroupName ? `${v.practiceGroupName} > ${v.name}` : v.name,
        })),
    ];
  }, [practiceAreaUniques?.values, mode]);

  const publicationOptions = useMemo(() => {
    const base = [{ value: "all", label: "All publications" }];
    if (mode === "new") {
      return [
        ...base,
        ...(publicationUniques?.values ?? [])
          .filter(v => taxonomyOptionFitsMode("publication", v, mode))
          .map(v => ({
            value: taxonomyOptionValue("publication", v, mode),
            label: publicationOptionLabel(v),
          })),
      ];
    }
    const pubs = publicationsData?.publications ?? [];
    return [
      ...base,
      ...pubs.map((p: LegacyPublication) => ({
        value: String(p.id),
        label: p.publication
          ? `${p.publication} (${p.edition ?? "n/a"})`
          : `Publication ${p.id}${p.edition ? ` (${p.edition})` : ""}`,
      })),
    ];
  }, [publicationsData?.publications, publicationUniques?.values, mode]);

  const FILTER_CONFIGS = [
    {
      key: "edition",
      label: "Edition",
      options: editionOptions,
    },
    {
      key: "publication",
      label: "Publication",
      options: publicationOptions,
    },
    {
      key: "region",
      label: "Region",
      options: regionOptions,
    },
    {
      key: "practiceArea",
      label: "Practice Area",
      options: practiceAreaOptions,
    },
    ...(contactLinkEnabled
      ? [
          {
            key: "contactMatch",
            label: "Contact match",
            options: CONTACT_MATCH_FILTER_OPTIONS,
            title: "Lawyer submissions only; limits the list to approved accounts",
          },
        ]
      : []),
  ];

  const handleFilterChangeWithDeps = (key: string, value: string | number | boolean) => {
    setCurrentPage(1);

    if (key === "edition") {
      handleFilterChange("edition", value);
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    if (key === "publication") {
      handleFilterChange("publication", value);
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    if (key === "region") {
      handleFilterChange(key, value);
      return;
    }

    if (key === "practiceArea") {
      handleFilterChange(key, value);
      return;
    }

    handleFilterChange(key, value);
  };

  const handleTaxonomyChange = (next: TaxonomyMode) => {
    setFilters(prev => ({ ...prev, publication: "all", region: "all", practiceArea: "all" }));
    setCurrentPage(1);
    updateUrl({
      [TAXONOMY_URL_PARAM]: next,
      publication: "all",
      region: "all",
      practiceArea: "all",
      page: "1",
    });
  };

  const handleClearAll = () => {
    setFilters({
      publication: "all",
      region: "all",
      edition: String(currentYear),
      practiceArea: "all",
      contactMatch: "all",
    });
    setSearchTerm("");
    setCurrentPage(1);
    setSortField(null);
    setSortDirection("asc");
    setHideIncomplete(false);
    updateUrl({
      publication: "all",
      region: "all",
      edition: String(currentYear),
      practiceArea: "all",
      contactMatch: "all",
      search: "",
      page: "1",
      sortField: "",
      sortDirection: "",
      hideIncomplete: "false",
    });
  };

  const handleClearFilterWithDeps = (key: string) => {
    setCurrentPage(1);

    if (key === "edition") {
      handleFilterChange("edition", String(currentYear));
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    if (key === "publication") {
      handleFilterChange("publication", "all");
      handleFilterChange("region", "all");
      handleFilterChange("practiceArea", "all");
      return;
    }

    if (key === "region") {
      handleFilterChange("region", "all");
      return;
    }

    if (key === "practiceArea") {
      handleFilterChange("practiceArea", "all");
      return;
    }

    handleClearFilter?.(key);
  };

  const totalPages = Math.ceil(pagination.total / ITEMS_PER_PAGE);
  const startIndex = pagination.offset;
  const endIndex = Math.min(startIndex + submissions.length, pagination.total);

  const enhancedActiveFilters = useMemo(() => {
    return activeFilters.map(filter => {
      if (filter.key === "publication") {
        const match = publicationOptions.find(opt => String(opt.value) === filters.publication);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "region") {
        const match = regionOptions.find(opt => String(opt.value) === filters.region);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "practiceArea") {
        const match = practiceAreaOptions.find(opt => String(opt.value) === filters.practiceArea);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      if (filter.key === "contactMatch") {
        const match = CONTACT_MATCH_FILTER_OPTIONS.find(opt => opt.value === filters.contactMatch);
        return {
          ...filter,
          displayValue: match?.label ?? String(filter.value),
        } as const;
      }
      return { ...filter, displayValue: filter.displayValue ?? String(filter.value) } as const;
    });
  }, [
    activeFilters,
    filters.publication,
    filters.region,
    filters.practiceArea,
    filters.contactMatch,
    publicationOptions,
    regionOptions,
    practiceAreaOptions,
  ]);

  if (error) {
    const status = (error as Error & { status?: number }).status;
    const isAuthError = status === 401 || status === 403;
    if (isAuthError) {
      return <UnauthorizedView />;
    }
  }

  return (
    <div className={styles.wrapper}>
      <div>
        <PageHeader title="Submissions">
          <div className={styles.headerActions}>
            <Button
              size="small"
              variant="outlined"
              color="primary"
              onClick={() => {
                void handleStartBulkParse();
              }}
              title="Parse all unparsed files matching the current filters"
            >
              Parse all (filtered)
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={submissionsExport.exportData}
              disabled={submissionsExport.isExporting || pagination.total === 0}
            >
              {submissionsExport.isExporting ? "Exporting..." : "Export submissions"}
            </Button>
          </div>
        </PageHeader>
      </div>

      {toggleVisible && <TaxonomyToggle mode={mode} onChange={handleTaxonomyChange} />}

      <DataFilters
        filters={FILTER_CONFIGS}
        values={filters}
        onChange={handleFilterChangeWithDeps}
        activeFilters={enhancedActiveFilters}
        onClearFilter={handleClearFilterWithDeps}
        onClearAll={handleClearAll}
      >
        <div className={styles.filtersBottomRow}>
          <Checkbox
            checked={hideIncomplete}
            onChange={e => {
              const next = e.target.checked;
              setHideIncomplete(next);
              updateUrl({ hideIncomplete: next ? "true" : "false" });
            }}
            label="Hide incomplete submissions"
          />
          <div className={styles.rowsPerPageControl}>
            <label htmlFor="itemsPerPage" className={styles.rowsPerPageLabel}>
              Rows per page:
            </label>
            <span>{ITEMS_PER_PAGE}</span>
          </div>
        </div>
      </DataFilters>

      <div className={styles.searchSection}>
        <SearchBar
          value={searchTerm}
          onChange={value => {
            setSearchTerm(value);
            setCurrentPage(1);
            updateUrl({ search: value, page: "1" });
          }}
          onClear={() => {
            setSearchTerm("");
            setCurrentPage(1);
            updateUrl({ search: "" });
          }}
          placeholder="Search by firm name..."
        />
      </div>

      {isLoading && <LoadingState message="Loading submissions..." />}

      {error && !isLoading && (
        <EmptyState
          variant="error"
          title="Failed to load submissions"
          message={error.message}
          action={{
            label: "Retry",
            onClick: () => window.location.reload(),
          }}
        />
      )}

      {translationsError && !isLoading && (
        <EmptyState
          variant="info"
          title="Translations failed to load"
          message={translationsError.message}
        />
      )}

      {submissionsExport.isExporting && submissionsExport.progress && (
        <ExportProgress
          label="Exporting submissions..."
          entityName="submissions"
          progress={submissionsExport.progress}
        />
      )}

      {submissionsExport.error && !submissionsExport.isExporting && (
        <EmptyState variant="error" title="Export failed" message={submissionsExport.error} />
      )}

      {!isLoading && !error && (
        <>
          <DataTable
            data={filteredSubmissions}
            columns={columns}
            size="small"
            className={submissionStyles.submissionsTable}
            maxHeight="55vh"
            // One row per entry: a submission with several lawyers repeats its submissionId.
            rowKey="submissionEntryId"
            sortableColumnKeys={sortableColumnKeys}
            sortField={sortField}
            sortDirection={sortDirection}
            onSort={handleSort}
          />

          <PaginationControls
            currentPage={currentPage}
            totalPages={totalPages}
            startIndex={startIndex + 1}
            endIndex={endIndex}
            totalCount={pagination.total}
            itemName="submissions"
            onPageChange={page => {
              setCurrentPage(page);
              updateUrl({ page: String(page) });
            }}
          />
        </>
      )}

      {bulkParse && (
        <BulkParseProgressModal
          phase={bulkParse.phase}
          total={bulkParse.total}
          processed={bulkParse.processed}
          enqueued={bulkParse.enqueued}
          skipped={bulkParse.skipped}
          errorMessage={bulkParse.errorMessage}
          cancelRequested={bulkParse.cancelRequested}
          onConfirm={() => {
            void handleConfirmBulkParse();
          }}
          onCancelRun={handleCancelBulkParseRun}
          onClose={handleCloseBulkParse}
        />
      )}

      {linkContactTarget && (
        <LinkSubmissionContactModal
          submission={linkContactTarget.row}
          preselectedContactId={linkContactTarget.preselectedContactId}
          returnTo={linkContactReturnTo}
          onClose={() => setLinkContactTarget(null)}
          onChanged={() => {
            void refetch();
          }}
        />
      )}

      <DeleteSubmissionModal
        submission={deleteFlow.target}
        isOpen={deleteFlow.isDeleteModalOpen}
        onClose={deleteFlow.closeDelete}
        onConfirm={() => {
          void deleteFlow.confirmDelete();
        }}
        isDeleting={deleteFlow.isDeleting}
      />

      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
          duration={toast.type === "error" ? 5000 : 3000}
        />
      )}
    </div>
  );
}

export default SubmissionsList;
